<template>
  <div class="study-questions-container">
    <div class="mb-7 mt-7">
      <v-text-field
        v-model="studyQuestionStatement"
        :placeholder="
          $t('unitBuilder.studyQuestions.introStatementPlaceholder')
        "
        :hint="$t('unitBuilder.studyQuestions.introStatementHint')"
        prepend-inner-icon="mdi-text-short"
        hide-details="auto"
        color="#a5d867"
        class="mb-7 mt-7"
        variant="underlined"
        style="min-width: 100%"
      ></v-text-field>
      <v-text-field
        v-model="studyQuestion"
        :placeholder="$t('unitBuilder.studyQuestions.questionPlaceholder')"
        :hint="$t('unitBuilder.studyQuestions.questionHint')"
        hide-details="auto"
        prepend-inner-icon="mdi-message-question-outline"
        color="#a5d867"
        class="mb-7"
        variant="underlined"
        style="min-width: 100%"
      ></v-text-field>
      <v-card-text
        class="pa-0 pt-2 text-subtitle-1 font-weight-bold text-uppercase"
        >{{ $t('unitBuilder.studyQuestions.additionalResource') }}</v-card-text
      >
      <v-file-input
        v-model="studyQuestionResource"
        clearable
        accept=".pdf,.jpg,.jpeg,.png"
        color="#a5d867"
        counter
        :hint="$t('unitBuilder.studyQuestions.fileInputHint')"
        persistent-hint
        variant="underlined"
        :placeholder="$t('unitBuilder.studyQuestions.fileInputPlaceholder')"
        prepend-icon="mdi-paperclip"
        :show-size="1000"
        style="min-width: 100%"
      >
        <template #selection="{ index, text }">
          <v-chip v-if="index < 2" color="#a5d867" label size="small">
            {{ text }}
          </v-chip>

          <span
            v-else-if="index === 2"
            class="text-overline text-grey-darken-3 mx-2"
          >
            +{{ studyQuestionResource.length - 2 }}
            {{ $t('unitBuilder.studyQuestions.files') }}
          </span>
        </template>
      </v-file-input>
      <v-text-field
        v-if="studyQuestionResource"
        v-model="studyQuestionResourceLabel"
        :placeholder="$t('unitBuilder.studyQuestions.resourceNamePlaceholder')"
        prepend-inner-icon="mdi-button-pointer"
        :hint="$t('unitBuilder.studyQuestions.resourceNameHint')"
        hide-details="auto"
        color="#a5d867"
        class="mt-3 mb-7"
        style="min-width: 100%"
      ></v-text-field>
      <v-divider class="mt-8 mb-1"></v-divider>
      <div class="v-text-field custom v-text-field--outlined">
        <label class="v-label">{{
          $t('unitBuilder.studyQuestions.userResponseLength')
        }}</label>
      </div>
      <v-radio-group
        v-model="studyQuestionResponseLines"
        inline
        density="default"
        mandatory
        class="response-lines pt-1"
      >
        <v-radio
          color="#a5d867"
          :label="$t('unitBuilder.studyQuestions.short')"
          value="short"
        ></v-radio>
        <v-radio
          color="#a5d867"
          :label="$t('unitBuilder.studyQuestions.medium')"
          value="medium"
        ></v-radio>
        <v-radio
          color="#a5d867"
          :label="$t('unitBuilder.studyQuestions.long')"
          value="long"
        ></v-radio>
      </v-radio-group>
      <div class="text-center">
        <v-btn
          color="#cfebae"
          class="elevation-0 font-weight-bold"
          @click="addStudyQuestion"
          >{{ $t('unitBuilder.studyQuestions.addStudyQuestion') }}</v-btn
        >
      </div>
      <v-alert
        v-if="studyQuestionFeedback"
        border="start"
        color="#592892"
        class="text-center mt-7"
      >
        {{ studyQuestionFeedback }}
      </v-alert>
      <v-divider v-if="studyquestions.length" class="mt-10"></v-divider>
    </div>
    <template v-if="studyquestions.length">
      <v-expansion-panels
        v-model="openedStudyPanels"
        multiple
        variant="accordion"
        tile
        class="pt-0 pb-0"
        :style="{
          background: this.$vuetify.theme.dark ? '#1e1e1e' : '#ffffff',
        }"
      >
        <draggable
          v-model="studyquestions"
          item-key="id"
          handle=".handle"
          @start="drag = true"
          @end="drag = false"
          @change="updatePositions"
          :tag="'div'"
        >
          <template #item="{ element, index }">
            <div>
              <v-expansion-panel
                :value="panelValueFor(element, index)"
                :key="element.id"
                elevation="1"
                class="mb-3"
              >
                <div class="expansion-panel-header-container">
                  <div class="handle"><v-icon>drag_icon</v-icon></div>
                  <span class="panel-header font-weight-bold">{{
                    element.studyQuestion
                  }}</span>
                  <div class="action-icons">
                    <v-icon @click.stop="togglePanel(panelValueFor(element, index))"
                      >edit</v-icon
                    >
                    <v-icon @click.stop="deleteItem(element)"
                      >delete_icon</v-icon
                    >
                  </div>
                </div>
                <v-expansion-panel-text class="pl-5 pr-5">
                  <v-text-field
                    v-model="element.studyQuestionStatement"
                    :placeholder="
                      $t('unitBuilder.studyQuestions.introStatementHint')
                    "
                    :hint="
                      $t('unitBuilder.studyQuestions.introStatementPlaceholder')
                    "
                    persistent-hint
                    prepend-inner-icon="mdi-text-short"
                    hide-details="auto"
                    color="#a5d867"
                    class="mb-7"
                    variant="underlined"
                    @update:model-value="onQuestionChange(element, index)"
                    style="min-width: 100%"
                  />
                  <v-text-field
                    v-model="element.studyQuestion"
                    :placeholder="$t('unitBuilder.studyQuestions.questionHint')"
                    :hint="$t('unitBuilder.studyQuestions.questionPlaceholder')"
                    persistent-hint
                    prepend-inner-icon="mdi-message-question-outline"
                    hide-details="auto"
                    color="#a5d867"
                    class="mb-7"
                    variant="underlined"
                    @update:model-value="onQuestionChange(element, index)"
                    style="min-width: 100%"
                  />
                  <v-text-field
                    v-model="element.studyQuestionResourceLabel"
                    :placeholder="
                      $t('unitBuilder.studyQuestions.resourceNameHint')
                    "
                    :hint="
                      $t('unitBuilder.studyQuestions.resourceNamePlaceholder')
                    "
                    persistent-hint
                    prepend-inner-icon="mdi-button-pointer"
                    hide-details="auto"
                    color="#a5d867"
                    class="mb-3"
                    variant="underlined"
                    @update:model-value="onQuestionChange(element, index)"
                    style="min-width: 100%"
                  />
                  <v-chip
                    v-if="element.studyQuestionResourceLabel"
                    :href="element.studyQuestionResourceURL"
                    target="_blank"
                    size="small"
                    class="font-weight-bold btn-multiline mb-1 btn-multiline"
                  >
                    {{ element.studyQuestionResourceLabel }}
                  </v-chip>
                  <v-list-item
                    v-else
                    :href="element.studyQuestionResourceURL"
                    target="_blank"
                  >
                    {{ element.studyQuestionResourceURL }}
                  </v-list-item>
                  <v-radio-group
                    v-model="element.studyQuestionResponseLines"
                    inline
                    density="compact"
                    :hint="$t('unitBuilder.studyQuestions.responseLengthHint')"
                    persistent-hint
                    mandatory
                    class="response-lines"
                    @update:model-value="onQuestionChange(element, index)"
                  >
                    <v-radio
                      color="#a5d867"
                      :label="$t('unitBuilder.studyQuestions.short')"
                      value="short"
                    />
                    <v-radio
                      color="#a5d867"
                      :label="$t('unitBuilder.studyQuestions.medium')"
                      value="medium"
                    />
                    <v-radio
                      color="#a5d867"
                      :label="$t('unitBuilder.studyQuestions.long')"
                      value="long"
                    />
                  </v-radio-group>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </div>
          </template>
        </draggable>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
  import firebase from 'firebase'
  import draggable from 'vuedraggable'
  import { confirmAction } from '@/components/utilities/SwalService'
  import { matchesConfirmWord } from '@/helpers/confirmWord'

  export default {
    name: 'StudyQuestions',
    props: ['section', 'backdoor', 'unitbackdoor', 'currentLang', 'data'],
    data() {
      return {
        studyQuestionFeedback: null,
        // Panel open state must use the same values as v-expansion-panel :value
        // (question ids), not array indices — mixing broke Vuetify 3 v-model after edit click.
        openedStudyPanels: [],
        studyquestions: [],
        studyQuestionStatement: '',
        studyQuestion: '',
        studyQuestionResponseLines: '',
        studyQuestionResource: null,
        uploadedStudyQuestionResource: '',
        studyQuestionResourceLabel: '',
      }
    },
    components: {
      draggable,
    },
    watch: {
      backdoor: function (newVal, oldVal) {
        // watch it
        // console.log('Prop changed: ', newVal, ' | was: ', oldVal)
        if (newVal > oldVal) this.clearComponent()
      },
      unitbackdoor: function (newVal, oldVal) {
        // watch it
        // console.log('unitbackdoor changed: ', newVal, ' | was: ', oldVal)
        if (newVal > oldVal) this.init()
      },
      // Watch for language changes
      currentLang: function (newLang, oldLang) {
        if (newLang !== oldLang) {
          this.loadLanguageData()
        }
      },
      // Watch for data changes from parent
      data: {
        handler: function (newData) {
          // If data is an array (even if empty), use it
          // If undefined/null, set to empty array (don't keep old data)
          if (newData && Array.isArray(newData)) {
            this.studyquestions = [...newData]
            this.openedStudyPanels = this.allPanelKeys()
          } else {
            this.studyquestions = []
            this.openedStudyPanels = []
          }
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      /** Stable key for v-expansion-panel :value — must match openedStudyPanels entries. */
      panelValueFor(q, index = 0) {
        if (q != null && q.id != null && String(q.id) !== '') {
          return String(q.id)
        }
        return `__idx_${index}`
      },
      allPanelKeys() {
        return this.studyquestions
          .map((q, i) => this.panelValueFor(q, i))
          .filter(Boolean)
      },
      loadLanguageData() {
        // Load data for the current language
        // If data is an array (even if empty), use it
        // If undefined/null, use empty array (don't check localStorage for language-aware components)
        if (this.data && Array.isArray(this.data)) {
          this.studyquestions = [...this.data]
          this.openedStudyPanels = this.allPanelKeys()
        } else {
          this.studyquestions = []
          this.openedStudyPanels = []
        }
        // Update parent with current state
        this.emitToParent()
      },
      async addStudyQuestion() {
        this.studyQuestionFeedback = ''
        let quest = this.studyQuestion
        let statement = this.studyQuestionStatement
        let numOfLines = this.studyQuestionResponseLines

        if (quest) {
          const newId = Date.now()
          this.studyquestions.push({
            id: newId,
            studyQuestionStatement: statement,
            studyQuestion: quest,
            studyQuestionResponseLines: numOfLines,
          })

          this.openedStudyPanels = [...this.allPanelKeys()]

          // update the localStorage
          this.addToLocalStorage()

          // clear fields
          this.studyQuestion = ''
          this.studyQuestionStatement = ''
          this.studyQuestionResource = null
          this.studyQuestionResourceLabel = ''
        } else {
          this.studyQuestionFeedback = this.$t(
            'unitBuilder.studyQuestions.needToAddQuestion'
          )
        }

        // console.log('1 ', this.studyquestions)
      },
      clearComponent() {
        localStorage.removeItem(this.section)

        this.studyquestions = []
        this.openedStudyPanels = []

        // update parent
        this.emitToParent()
      },
      addToLocalStorage() {
        // update the localStorage
        if (this.studyquestions.length)
          localStorage.setItem(
            this.section,
            JSON.stringify(this.studyquestions)
          )
        else localStorage.removeItem(this.section)

        // update parent
        this.emitToParent()
      },
      async deleteItem(item) {
        if (!item) {
          await confirmAction({
            title: this.$t('unitBuilder.studyQuestions.houstonProblem'),
            text: this.$t(
              'unitBuilder.studyQuestions.deleteErrorMissingParams'
            ),
            showCancelButton: false,
            context: this,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })
          return
        }

        const title = this.$t('unitBuilder.studyQuestions.deleteConfirmTitle', {
          question: item.studyQuestion,
        })
        const confirmText = this.$t(
          'unitBuilder.studyQuestions.deleteConfirmText'
        )
        const inputValidator = (value) => {
          return new Promise((resolve) => {
            if (matchesConfirmWord(value, 'DELETE')) resolve()
            else resolve(this.$t('unitBuilder.studyQuestions.needToTypeDelete'))
          })
        }

        if (item.studyQuestionResourceURL) {
          const confirmed = await confirmAction({
            title,
            html: confirmText,
            input: 'text',
            inputPlaceholder: this.$t(
              'unitBuilder.studyQuestions.deleteConfirmPlaceholder'
            ),
            inputValidator,
            confirmButtonText: this.$t(
              'unitBuilder.studyQuestions.deleteConfirmButton'
            ),
            cancelButtonText: this.$t(
              'unitBuilder.studyQuestions.deleteCancelButton'
            ),
            context: this,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })

          if (!confirmed) return

          const images = []
          if (item.studyQuestionResourceName) {
            images.push(item.studyQuestionResourceName)
          }

          this.removeFile(item, images, 'removeall')
          return
        }

        // If no resource to delete, proceed to remove from array + localStorage
        const confirmed = await confirmAction({
          title,
          html: confirmText,
          input: 'text',
          inputPlaceholder: this.$t(
            'unitBuilder.studyQuestions.deleteConfirmPlaceholder'
          ),
          inputValidator,
          confirmButtonText: this.$t(
            'unitBuilder.studyQuestions.deleteConfirmButton'
          ),
          cancelButtonText: this.$t(
            'unitBuilder.studyQuestions.deleteCancelButton'
          ),
          context: this,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })

        if (!confirmed) return

        const removedKey = this.panelValueFor(
          item,
          this.studyquestions.indexOf(item)
        )
        this.studyquestions.splice(this.studyquestions.indexOf(item), 1)
        this.openedStudyPanels = this.openedStudyPanels.filter(
          (k) => String(k) !== String(removedKey)
        )
        this.addToLocalStorage()
      },
      emitToParent() {
        // Emit this information to the parent component
        this.$emit('allstudyquestions', {
          section: this.section,
          questions: this.studyquestions,
        })
      },
      onQuestionChange(item, key) {
        const statusIndicator = document.querySelectorAll(
          '.snippet-autosave-status-' + key + ', .editor-' + key
        )
        statusIndicator.forEach(function (item) {
          item.classList.add('busy')
        })

        // update localStorage
        this.addToLocalStorage()

        setTimeout(() => {
          statusIndicator.forEach(function (item) {
            item.classList.remove('busy')
          })
        }, 500)
      },
      togglePanel(key) {
        const k = String(key)
        const open = Array.isArray(this.openedStudyPanels)
          ? this.openedStudyPanels.map((k) => String(k))
          : this.openedStudyPanels != null && this.openedStudyPanels !== ''
            ? [String(this.openedStudyPanels)]
            : []
        const i = open.indexOf(k)
        if (i >= 0) open.splice(i, 1)
        else open.push(k)
        this.openedStudyPanels = open
      },
      updatePositions() {
        // update localStorage
        this.addToLocalStorage()
      },
      async uploadFile(item) {
        // console.log('uploadFile ', item)
        var splitfile = item.name.split('.')
        var newname = splitfile[0] + '_' + Date.now() + '.' + splitfile[1]
        var storage = firebase.storage()

        let uploadedFileMetadata = {}

        const snapshot = await storage.ref('units/' + newname).put(item)
        uploadedFileMetadata = snapshot.metadata
        // console.log('snapshot ', snapshot)
        const downloadURL = await snapshot.ref.getDownloadURL()
        uploadedFileMetadata.publicURL = downloadURL
        // console.log('downloadURL ', uploadedFileMetadata)
        return uploadedFileMetadata
      },
      async removeFile(item, which, action) {
        // console.log('removeFile ', item, which, action)

        if (!item || !which || !action) {
          await confirmAction({
            title: this.$t('unitBuilder.studyQuestions.houstonProblem'),
            text: this.$t(
              'unitBuilder.studyQuestions.deleteErrorMissingParams'
            ),
            showCancelButton: false,
            context: this,
            buttonsStyling: false,
            customClass: {
              confirmButton:
                'swal-confirm-btn text-white text-uppercase text-body-2',
              cancelButton:
                'swal-cancel-btn text-white text-uppercase text-body-2',
            },
          })
          return
        }

        if (action === 'removeall') {
          for (let index = 0; index < which.length; index++) {
            let fullPath = 'units/' + which[index]
            let storage = firebase.storage()
            let storageRef = storage.ref()
            let fileRef = storageRef.child(fullPath)

            try {
              await fileRef.delete()
              // console.log('file removed')

              const removedKey = this.panelValueFor(
                item,
                this.studyquestions.indexOf(item)
              )
              this.studyquestions.splice(this.studyquestions.indexOf(item), 1)
              this.openedStudyPanels = this.openedStudyPanels.filter(
                (k) => String(k) !== String(removedKey)
              )
              this.addToLocalStorage()
            } catch (error) {
              console.error(`file delete error occurred: ${error}`)
            }
          }

          return
        }

        if (action === 'removesingle') {
          let fullPath = 'units/' + which
          let storage = firebase.storage()
          let storageRef = storage.ref()
          let fileRef = storageRef.child(fullPath)

          try {
            await fileRef.delete()
            // console.log('single file removed')
            this.addToLocalStorage()
          } catch (error) {
            console.error(`file delete error occurred: ${error}`)
          }

          return
        }

        // fallback alert if action is unrecognized
        await confirmAction({
          title: this.$t('unitBuilder.studyQuestions.houstonProblem'),
          text: this.$t('unitBuilder.studyQuestions.deleteErrorUnknownAction'),
          showCancelButton: false,
          context: this,
          buttonsStyling: false,
          customClass: {
            confirmButton:
              'swal-confirm-btn text-white text-uppercase text-body-2',
            cancelButton:
              'swal-cancel-btn text-white text-uppercase text-body-2',
          },
        })
      },
      init() {
        this.studyquestions = []

        // If we have data from parent (language-specific), use that
        // Only check localStorage if we're NOT in a language-aware context (no currentLang prop)
        if (this.data && Array.isArray(this.data)) {
          // Use data prop if it's an array (even if empty)
          this.studyquestions = [...this.data]

          this.openedStudyPanels =
            this.studyquestions.length > 0 ? this.allPanelKeys() : []
        } else if (!this.currentLang && localStorage.getItem(this.section)) {
          // Only use localStorage for legacy support (when currentLang is not provided)
          this.studyquestions = JSON.parse(localStorage.getItem(this.section))
          this.openedStudyPanels =
            this.studyquestions.length > 0 ? this.allPanelKeys() : []
        } else {
          // Language-aware context with no data = empty array
          this.studyquestions = []
          this.openedStudyPanels = []
        }

        // update parent
        this.emitToParent()
      },
    },
    created() {
      this.init()
    },
  }
</script>

<style>
  .units-container .study-questions-container .v-expansion-panel {
    border: 1px solid #b5b5b5;
  }
  .study-questions-container .v-expansion-panels > div {
    width: 100%;
  }
  .study-questions-container .v-expansion-panel__header {
    cursor: grab;
  }
  .study-questions-container .response-lines .theme--light.v-messages {
    color: #000;
  }
  .study-questions-container .response-lines .v-label {
    font-size: 15px;
    font-weight: 300;
    text-transform: none;
  }
  .study-questions-container
    .v-expansion-panels
    .snippet-autosave-status.busy
    .snippet-autosave-status_spinner-label::after {
    content: 'Preparing...';
  }
  .study-questions-container
    .v-expansion-panels
    .snippet-autosave-status_spinner-label::after {
    content: 'Ready!';
  }
  /* .study-questions-container .custom {
    position: relative;
    margin-bottom: 0;
    padding-bottom: 0;
  } */
  .study-questions-container .custom .v-label {
    color: #000000;
    font-size: 12px;
    font-weight: 600;
    opacity: 1;
  }
  .study-questions-container .btn-multiline > span {
    width: 100%;
  }
</style>
